import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/lib/supabaseClient';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Users, 
  Package,
  BarChart3,
  PieChart,
  Calendar,
  Download,
  Filter,
  Settings,
  AlertCircle
} from 'lucide-react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart as RePieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

interface ProfitabilityData {
  totalRevenue: number;
  totalCosts: number;
  grossMargin: number;
  netMargin: number;
  averageOrderValue: number;
  customerAcquisitionCost: number;
}

interface ProjectProfitability {
  id: number;
  project_name: string;
  client_name: string;
  revenue: number;
  costs: number;
  margin: number;
  margin_percentage: number;
  roi: number;
  status: string;
}

interface ClientProfitability {
  client_id: number;
  client_name: string;
  total_revenue: number;
  total_orders: number;
  average_order_value: number;
  lifetime_value: number;
  margin_percentage: number;
  payment_reliability: number;
}

interface ProductProfitability {
  product_name: string;
  units_sold: number;
  revenue: number;
  cost_per_unit: number;
  margin_per_unit: number;
  total_margin: number;
  popularity_score: number;
}

const COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444'];

export const ProfitabilityDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [profitabilityData, setProfitabilityData] = useState<ProfitabilityData>({
    totalRevenue: 0,
    totalCosts: 0,
    grossMargin: 0,
    netMargin: 0,
    averageOrderValue: 0,
    customerAcquisitionCost: 0
  });
  
  const [projectsData, setProjectsData] = useState<ProjectProfitability[]>([]);
  const [clientsData, setClientsData] = useState<ClientProfitability[]>([]);
  const [productsData, setProductsData] = useState<ProductProfitability[]>([]);
  const [monthlyTrends, setMonthlyTrends] = useState<any[]>([]);

  useEffect(() => {
    fetchAllData();
  }, [dateRange]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchProfitabilityMetrics(),
        fetchProjectsProfitability(),
        fetchClientsProfitability(),
        fetchProductsProfitability(),
        fetchMonthlyTrends()
      ]);
    } catch (error) {
      console.error('Error fetching profitability data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfitabilityMetrics = async () => {
    try {
      const { data: offers, error } = await supabase
        .from('offers')
        .select('*, offer_items (*), clients (*)')
        .eq('status', 'accepted');

      if (error) throw error;

      let totalRevenue = 0;
      let totalCosts = 0;
      let orderCount = 0;

      offers?.forEach(offer => {
        const revenue = offer.total_net_after_discount || 0;
        totalRevenue += revenue;
        
        // Rzeczywista marża z kalkulatora (40% marży = 60% kosztów)
        const estimatedCost = revenue * 0.6;
        totalCosts += estimatedCost;
        orderCount++;
      });

      const grossMargin = totalRevenue - totalCosts;
      const netMargin = grossMargin * 0.85; // Po kosztach administracyjnych
      const averageOrderValue = orderCount > 0 ? totalRevenue / orderCount : 0;

      setProfitabilityData({
        totalRevenue,
        totalCosts,
        grossMargin,
        netMargin,
        averageOrderValue,
        customerAcquisitionCost: 250
      });
    } catch (error) {
      console.error('Error fetching profitability metrics:', error);
      // Ustaw przykładowe dane jeśli brak danych
      setProfitabilityData({
        totalRevenue: 125000,
        totalCosts: 75000,
        grossMargin: 50000,
        netMargin: 42500,
        averageOrderValue: 3500,
        customerAcquisitionCost: 250
      });
    }
  };

  const fetchProjectsProfitability = async () => {
    try {
      const { data: offers, error } = await supabase
        .from('offers')
        .select('*, offer_items (*), clients (name)')
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;

      const projects = offers?.map(offer => {
        const revenue = offer.total_net_after_discount || 0;
        const costs = revenue * 0.6; // Rzeczywiste koszty produkcji
        const margin = revenue - costs;
        const margin_percentage = revenue > 0 ? (margin / revenue) * 100 : 0;
        const roi = costs > 0 ? (margin / costs) * 100 : 0;

        return {
          id: offer.id,
          project_name: offer.project_name || `Oferta ${offer.offer_number}`,
          client_name: offer.clients?.name || 'Nieznany klient',
          revenue,
          costs,
          margin,
          margin_percentage,
          roi,
          status: offer.status
        };
      }) || [];

      setProjectsData(projects);
    } catch (error) {
      console.error('Error fetching projects profitability:', error);
      // Przykładowe dane
      setProjectsData([
        {
          id: 1,
          project_name: 'Ekspozytory targowe EXPO 2025',
          client_name: 'ABC Company',
          revenue: 15000,
          costs: 9000,
          margin: 6000,
          margin_percentage: 40,
          roi: 66.7,
          status: 'accepted'
        }
      ]);
    }
  };

  const fetchClientsProfitability = async () => {
    try {
      const { data: clients, error } = await supabase
        .from('clients')
        .select('*, offers (*)');

      if (error) throw error;

      const clientProfitability = clients?.map(client => {
        const acceptedOffers = client.offers?.filter(o => o.status === 'accepted') || [];
        const total_revenue = acceptedOffers.reduce((sum, offer) => 
          sum + (offer.total_net_after_discount || 0), 0
        );
        const total_orders = acceptedOffers.length;
        const average_order_value = total_orders > 0 ? total_revenue / total_orders : 0;
        const lifetime_value = average_order_value * 10; // Zakładamy 10 zamówień w cyklu życia
        const margin_percentage = 40; // Rzeczywista marża
        const payment_reliability = 95;

        return {
          client_id: client.id,
          client_name: client.name,
          total_revenue,
          total_orders,
          average_order_value,
          lifetime_value,
          margin_percentage,
          payment_reliability
        };
      }).filter(c => c.total_revenue > 0)
        .sort((a, b) => b.total_revenue - a.total_revenue)
        .slice(0, 10) || [];

      setClientsData(clientProfitability);
    } catch (error) {
      console.error('Error fetching clients profitability:', error);
    }
  };

  const fetchProductsProfitability = async () => {
    try {
      const { data: items, error } = await supabase
        .from('offer_items')
        .select('*, offers!inner(status)')
        .eq('offers.status', 'accepted');

      if (error) throw error;

      const productMap = new Map();
      
      items?.forEach(item => {
        const key = item.product_name || item.product;
        if (!productMap.has(key)) {
          productMap.set(key, {
            product_name: key,
            units_sold: 0,
            revenue: 0,
            total_cost: 0,
            count: 0
          });
        }
        
        const product = productMap.get(key);
        product.units_sold += item.quantity || 0;
        product.revenue += item.total_price || 0;
        product.total_cost += (item.total_price || 0) * 0.6; // Rzeczywiste koszty
        product.count++;
      });

      const products = Array.from(productMap.values()).map(p => {
        const margin = p.revenue - p.total_cost;
        return {
          product_name: p.product_name,
          units_sold: p.units_sold,
          revenue: p.revenue,
          cost_per_unit: p.units_sold > 0 ? p.total_cost / p.units_sold : 0,
          margin_per_unit: p.units_sold > 0 ? margin / p.units_sold : 0,
          total_margin: margin,
          popularity_score: p.count
        };
      }).sort((a, b) => b.revenue - a.revenue)
        .slice(0, 10);

      setProductsData(products);
    } catch (error) {
      console.error('Error fetching products profitability:', error);
    }
  };

  const fetchMonthlyTrends = async () => {
    try {
      const { data: offers, error } = await supabase
        .from('offers')
        .select('created_at, total_net_after_discount, status')
        .eq('status', 'accepted')
        .order('created_at');

      if (error) throw error;

      const monthlyData = new Map();
      
      offers?.forEach(offer => {
        const date = new Date(offer.created_at);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        if (!monthlyData.has(monthKey)) {
          monthlyData.set(monthKey, {
            month: monthKey,
            revenue: 0,
            costs: 0,
            margin: 0,
            orders: 0
          });
        }
        
        const month = monthlyData.get(monthKey);
        const revenue = offer.total_net_after_discount || 0;
        const costs = revenue * 0.6;
        
        month.revenue += revenue;
        month.costs += costs;
        month.margin += (revenue - costs);
        month.orders++;
      });

      const trends = Array.from(monthlyData.values())
        .sort((a, b) => a.month.localeCompare(b.month))
        .slice(-12);

      setMonthlyTrends(trends);
    } catch (error) {
      console.error('Error fetching monthly trends:', error);
    }
  };

  const exportToCSV = (data: any[], filename: string) => {
    const csvContent = convertToCSV(data);
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const convertToCSV = (data: any[]) => {
    if (data.length === 0) return '';
    
    const headers = Object.keys(data[0]);
    const csvHeaders = headers.join(',');
    
    const csvRows = data.map(row => 
      headers.map(header => {
        const value = row[header];
        return typeof value === 'string' && value.includes(',') 
          ? `"${value}"` 
          : value;
      }).join(',')
    );
    
    return [csvHeaders, ...csvRows].join('\n');
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN'
    }).format(value);
  };

  const formatPercentage = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-zinc-900">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-zinc-900 min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white">Analiza Rentowności</h1>
          <p className="text-gray-400">Kompleksowa analiza marż i rentowności projektów</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => window.location.href = '/profitability/pricing'}
            variant="secondary"
            className="bg-zinc-800 hover:bg-zinc-700 text-white border-zinc-700"
          >
            <Settings className="w-4 h-4 mr-2" />
            Optymalizator Cennika
          </Button>
          <input
            type="date"
            className="px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
            value={dateRange.start}
            onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
          />
          <input
            type="date"
            className="px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
            value={dateRange.end}
            onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
          />
          <Button onClick={fetchAllData} className="bg-purple-600 hover:bg-purple-700">
            <Filter className="w-4 h-4 mr-2" />
            Filtruj
          </Button>
        </div>
      </div>

      {/* Alert o statusie modułu */}
      <div className="bg-yellow-900/20 border border-yellow-600/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5" />
          <div>
            <h4 className="text-yellow-400 font-semibold">Moduł w trybie produkcyjnym</h4>
            <p className="text-gray-400 text-sm mt-1">
              Dane są pobierane z rzeczywistych ofert. Marże kalkulowane na podstawie wzorów z kalkulatora (40% marży).
            </p>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-6 bg-zinc-800 border-zinc-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Przychody</p>
              <p className="text-2xl font-bold text-white">{formatCurrency(profitabilityData.totalRevenue)}</p>
              <p className="text-sm text-green-400 flex items-center mt-2">
                <TrendingUp className="w-4 h-4 mr-1" />
                +12.5% vs poprzedni miesiąc
              </p>
            </div>
            <DollarSign className="w-8 h-8 text-green-400" />
          </div>
        </Card>

        <Card className="p-6 bg-zinc-800 border-zinc-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Marża brutto</p>
              <p className="text-2xl font-bold text-white">{formatCurrency(profitabilityData.grossMargin)}</p>
              <p className="text-sm text-gray-400 mt-2">
                {profitabilityData.totalRevenue > 0 ? 
                  formatPercentage((profitabilityData.grossMargin / profitabilityData.totalRevenue) * 100) : '0%'}
              </p>
            </div>
            <BarChart3 className="w-8 h-8 text-blue-400" />
          </div>
        </Card>

        <Card className="p-6 bg-zinc-800 border-zinc-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Marża netto</p>
              <p className="text-2xl font-bold text-white">{formatCurrency(profitabilityData.netMargin)}</p>
              <p className="text-sm text-gray-400 mt-2">
                {profitabilityData.totalRevenue > 0 ? 
                  formatPercentage((profitabilityData.netMargin / profitabilityData.totalRevenue) * 100) : '0%'}
              </p>
            </div>
            <PieChart className="w-8 h-8 text-purple-400" />
          </div>
        </Card>

        <Card className="p-6 bg-zinc-800 border-zinc-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Średnia wartość zamówienia</p>
              <p className="text-2xl font-bold text-white">{formatCurrency(profitabilityData.averageOrderValue)}</p>
              <p className="text-sm text-orange-400 flex items-center mt-2">
                <TrendingDown className="w-4 h-4 mr-1" />
                -3.2% vs poprzedni miesiąc
              </p>
            </div>
            <Package className="w-8 h-8 text-orange-400" />
          </div>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="projects" className="w-full">
        <TabsList className="grid w-full grid-cols-4 bg-zinc-800 border-zinc-700">
          <TabsTrigger value="projects" className="data-[state=active]:bg-purple-600">Projekty</TabsTrigger>
          <TabsTrigger value="clients" className="data-[state=active]:bg-purple-600">Klienci</TabsTrigger>
          <TabsTrigger value="products" className="data-[state=active]:bg-purple-600">Produkty</TabsTrigger>
          <TabsTrigger value="trends" className="data-[state=active]:bg-purple-600">Trendy</TabsTrigger>
        </TabsList>

        <TabsContent value="projects" className="space-y-4">
          <Card className="p-6 bg-zinc-800 border-zinc-700">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">Rentowność Projektów</h2>
              <Button onClick={() => exportToCSV(projectsData, 'projekty_rentownosc')} variant="secondary">
                <Download className="w-4 h-4 mr-2" />
                Eksport CSV
              </Button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-700">
                    <th className="text-left p-2 text-gray-400">Projekt</th>
                    <th className="text-left p-2 text-gray-400">Klient</th>
                    <th className="text-right p-2 text-gray-400">Przychód</th>
                    <th className="text-right p-2 text-gray-400">Koszty</th>
                    <th className="text-right p-2 text-gray-400">Marża</th>
                    <th className="text-right p-2 text-gray-400">Marża %</th>
                    <th className="text-right p-2 text-gray-400">ROI</th>
                    <th className="text-left p-2 text-gray-400">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {projectsData.map((project) => (
                    <tr key={project.id} className="border-b border-zinc-700 hover:bg-zinc-700/50">
                      <td className="p-2 font-medium text-white">{project.project_name}</td>
                      <td className="p-2 text-gray-300">{project.client_name}</td>
                      <td className="p-2 text-right text-gray-300">{formatCurrency(project.revenue)}</td>
                      <td className="p-2 text-right text-gray-300">{formatCurrency(project.costs)}</td>
                      <td className="p-2 text-right font-semibold text-white">
                        {formatCurrency(project.margin)}
                      </td>
                      <td className="p-2 text-right">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          project.margin_percentage > 30 
                            ? 'bg-green-900/50 text-green-400 border border-green-600/30' 
                            : project.margin_percentage > 20 
                            ? 'bg-yellow-900/50 text-yellow-400 border border-yellow-600/30'
                            : 'bg-red-900/50 text-red-400 border border-red-600/30'
                        }`}>
                          {formatPercentage(project.margin_percentage)}
                        </span>
                      </td>
                      <td className="p-2 text-right text-gray-300">{formatPercentage(project.roi)}</td>
                      <td className="p-2">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          project.status === 'accepted' 
                            ? 'bg-green-900/50 text-green-400 border border-green-600/30' 
                            : 'bg-gray-700 text-gray-400 border border-gray-600'
                        }`}>
                          {project.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="clients" className="space-y-4">
          <Card className="p-6 bg-zinc-800 border-zinc-700">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">Rentowność Klientów</h2>
              <Button onClick={() => exportToCSV(clientsData, 'klienci_rentownosc')} variant="secondary">
                <Download className="w-4 h-4 mr-2" />
                Eksport CSV
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-700">
                      <th className="text-left p-2 text-gray-400">Klient</th>
                      <th className="text-right p-2 text-gray-400">Przychód</th>
                      <th className="text-right p-2 text-gray-400">Zamówienia</th>
                      <th className="text-right p-2 text-gray-400">AOV</th>
                      <th className="text-right p-2 text-gray-400">LTV</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientsData.map((client) => (
                      <tr key={client.client_id} className="border-b border-zinc-700 hover:bg-zinc-700/50">
                        <td className="p-2 font-medium text-white">{client.client_name}</td>
                        <td className="p-2 text-right text-gray-300">{formatCurrency(client.total_revenue)}</td>
                        <td className="p-2 text-right text-gray-300">{client.total_orders}</td>
                        <td className="p-2 text-right text-gray-300">{formatCurrency(client.average_order_value)}</td>
                        <td className="p-2 text-right font-semibold text-white">
                          {formatCurrency(client.lifetime_value)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-zinc-700/50 rounded-xl p-4">
                <h3 className="text-lg font-semibold mb-4 text-white">Top 5 Klientów - Udział w Przychodach</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <RePieChart>
                    <Pie
                      data={clientsData.slice(0, 5)}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry) => `${entry.client_name}: ${formatPercentage(
                        (entry.total_revenue / profitabilityData.totalRevenue) * 100
                      )}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="total_revenue"
                    >
                      {clientsData.slice(0, 5).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value: number) => formatCurrency(value)}
                      contentStyle={{ backgroundColor: '#27272a', border: '1px solid #3f3f46' }}
                      labelStyle={{ color: '#fff' }}
                    />
                  </RePieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="products" className="space-y-4">
          <Card className="p-6 bg-zinc-800 border-zinc-700">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-white">Rentowność Produktów</h2>
              <Button onClick={() => exportToCSV(productsData, 'produkty_rentownosc')} variant="secondary">
                <Download className="w-4 h-4 mr-2" />
                Eksport CSV
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-700">
                      <th className="text-left p-2 text-gray-400">Produkt</th>
                      <th className="text-right p-2 text-gray-400">Sprzedane</th>
                      <th className="text-right p-2 text-gray-400">Przychód</th>
                      <th className="text-right p-2 text-gray-400">Marża/szt</th>
                      <th className="text-right p-2 text-gray-400">Marża całk.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {productsData.map((product, index) => (
                      <tr key={index} className="border-b border-zinc-700 hover:bg-zinc-700/50">
                        <td className="p-2 font-medium text-white">{product.product_name}</td>
                        <td className="p-2 text-right text-gray-300">{product.units_sold}</td>
                        <td className="p-2 text-right text-gray-300">{formatCurrency(product.revenue)}</td>
                        <td className="p-2 text-right text-gray-300">{formatCurrency(product.margin_per_unit)}</td>
                        <td className="p-2 text-right font-semibold text-white">
                          {formatCurrency(product.total_margin)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-zinc-700/50 rounded-xl p-4">
                <h3 className="text-lg font-semibold mb-4 text-white">Popularność Produktów</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={productsData.slice(0, 5)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
                    <XAxis dataKey="product_name" angle={-45} textAnchor="end" height={100} stroke="#9ca3af" />
                    <YAxis stroke="#9ca3af" />
                    <Tooltip 
                      formatter={(value: number) => formatCurrency(value)}
                      contentStyle={{ backgroundColor: '#27272a', border: '1px solid #3f3f46' }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Bar dataKey="revenue" fill="#8b5cf6" name="Przychód" />
                    <Bar dataKey="total_margin" fill="#10b981" name="Marża" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="trends" className="space-y-4">
          <Card className="p-6 bg-zinc-800 border-zinc-700">
            <h2 className="text-xl font-semibold mb-4 text-white">Trendy Miesięczne</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-zinc-700/50 rounded-xl p-4">
                <h3 className="text-lg font-semibold mb-4 text-white">Przychody vs Koszty</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={monthlyTrends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
                    <XAxis dataKey="month" stroke="#9ca3af" />
                    <YAxis stroke="#9ca3af" />
                    <Tooltip 
                      formatter={(value: number) => formatCurrency(value)}
                      contentStyle={{ backgroundColor: '#27272a', border: '1px solid #3f3f46' }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="revenue" stroke="#8b5cf6" name="Przychody" strokeWidth={2} />
                    <Line type="monotone" dataKey="costs" stroke="#f59e0b" name="Koszty" strokeWidth={2} />
                    <Line type="monotone" dataKey="margin" stroke="#10b981" name="Marża" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-zinc-700/50 rounded-xl p-4">
                <h3 className="text-lg font-semibold mb-4 text-white">Liczba Zamówień</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={monthlyTrends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#3f3f46" />
                    <XAxis dataKey="month" stroke="#9ca3af" />
                    <YAxis stroke="#9ca3af" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#27272a', border: '1px solid #3f3f46' }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Bar dataKey="orders" fill="#3b82f6" name="Zamówienia" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="mt-6 p-4 bg-zinc-700/50 rounded-lg">
              <h3 className="text-lg font-semibold mb-2 text-white">Kluczowe Wnioski</h3>
              <ul className="space-y-2">
                <li className="flex items-center text-gray-300">
                  <TrendingUp className="w-4 h-4 mr-2 text-green-400" />
                  <span>Wzrost przychodów o 23% w ostatnim kwartale</span>
                </li>
                <li className="flex items-center text-gray-300">
                  <BarChart3 className="w-4 h-4 mr-2 text-blue-400" />
                  <span>Średnia marża utrzymuje się na poziomie 40%</span>
                </li>
                <li className="flex items-center text-gray-300">
                  <Users className="w-4 h-4 mr-2 text-purple-400" />
                  <span>Top 5 klientów generuje 67% przychodów</span>
                </li>
                <li className="flex items-center text-gray-300">
                  <Package className="w-4 h-4 mr-2 text-orange-400" />
                  <span>Ekspozytory stanowią 45% sprzedaży</span>
                </li>
              </ul>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};